import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CancelIcon from '@mui/icons-material/Cancel'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppTheme } from '@/theme/ThemeContext'
import SearchBar from '@/components/common/SearchBar'
import { useSearch } from '@/hooks/useSearch'
import {
  searchAlertConsumers,
  type APIConsumerRef,
} from '@/services/notificationService'
import { consumerLabel, useConsumerChips } from '../../consumerChips'

type Page = { items: APIConsumerRef[]; hasMore: boolean }
const NO_PAGE: Page = { items: [], hasMore: false }
// The node is 230px wide and its rows wrap to about two lines. Five rows and the hint fit the node at any
// canvas size, so the list needs no scroll bar.
const MAX_RESULTS = 5

/**
 * The value editor of a `consumers` attribute: type, and add the
 * consumers found. `values` are the water service ids the tree stores.
 */
const NodeConsumerPicker = ({
  values,
  onChange,
  disabled = false,
}: {
  values: string[]
  onChange: (values: string[]) => void
  disabled?: boolean
}) => {
  const { siteId } = useParams<{ siteId: string }>()
  const search = useSearch()
  const [page, setPage] = useState<Page | 'error'>(NO_PAGE)
  const { labelOf, remember } = useConsumerChips(siteId, values)

  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const colors = {
    text: isDarkMode ? '#FFFFFF' : '#003F61',
    border: isDarkMode ? '#445876' : 'black',
    chipBg: isDarkMode ? '#374151' : '#E3F5FF',
    chipText: isDarkMode ? '#FFFFFF' : '#003F61',
    bg: isDarkMode ? '#0D192F' : 'transparent',
    hover: isDarkMode ? '#1E2A44' : '#E3F5FF',
    error: isDarkMode ? '#F44336' : '#D32F2F',
  }
  const textStyle = {
    color: colors.text,
    fontFamily: 'Rubik, sans-serif',
    fontSize: '14px',
    fontWeight: 300,
    lineHeight: '120%',
  }

  // An empty text goes through the same path, so it also stops a request that is still running.
  const { run, query } = search
  useEffect(() => {
    run(
      signal =>
        query && siteId
          ? searchAlertConsumers(siteId, query, signal, MAX_RESULTS)
          : Promise.resolve(NO_PAGE),
      setPage,
      err => {
        console.error('[consumers] Search failed:', err)
        setPage('error')
      }
    )
  }, [siteId, query, run])

  // A cleared box shows nothing, whatever answer is still on its way or on screen.
  const searched = search.text.trim() !== ''
  const loading = searched && search.isSearching
  const failed = searched && !loading && page === 'error'
  const found = searched && !loading && page !== 'error' ? page : null

  const add = (picked: APIConsumerRef) => {
    remember(picked)
    const id = String(picked.serviceId)
    if (!values.includes(id)) onChange([...values, id])
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colors.border}`,
        padding: '5px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        width: '230px',
        maxWidth: '230px',
        boxSizing: 'border-box',
        direction: 'rtl',
        opacity: disabled ? 0.5 : 1,
        backgroundColor: colors.bg,
      }}
    >
      {values.length > 0 && (
        // nowheel: the list scrolls once it is long, and without it the wheel
        // zooms the canvas. nokey: Delete on a focused chip removes the chip;
        // the canvas must not also delete the node.
        <Box
          className="nowheel nokey"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            overflow: 'auto',
            maxHeight: '200px',
            gap: '4px',
          }}
        >
          {values.map(value => (
            <Chip
              key={value}
              label={labelOf(value)}
              size="small"
              onDelete={
                disabled
                  ? undefined
                  : () => onChange(values.filter(v => v !== value))
              }
              deleteIcon={<CancelIcon className="nodrag" />}
              sx={{
                backgroundColor: colors.chipBg,
                color: colors.chipText,
                height: 'auto',
                maxWidth: '100%',
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  padding: '3px 8px',
                },
              }}
            />
          ))}
        </Box>
      )}
      <Box className="nodrag">
        <SearchBar
          onSearch={search.setText}
          executeSearch={search.searchNow}
          width="100%"
          height={30}
          placeholder="שם, כתובת, מונה, כרטיס מים"
          sx={{
            opacity: disabled ? 0.6 : 1,
            pointerEvents: disabled ? 'none' : 'auto',
          }}
        />
      </Box>
      <Box className="nodrag nokey">
        {loading && <CircularProgress size={16} />}
        {failed && (
          <Typography
            role="alert"
            sx={{ ...textStyle, color: colors.error, fontSize: '12px' }}
          >
            החיפוש נכשל. נסה שוב.
          </Typography>
        )}
        {found && found.items.length === 0 && (
          <Typography sx={{ ...textStyle, fontSize: '12px' }}>
            לא נמצאו צרכנים
          </Typography>
        )}
        {found && found.items.length > 0 && (
          <Box role="list" sx={{ display: 'flex', flexDirection: 'column' }}>
            {found.items.slice(0, MAX_RESULTS).map(item => {
              const added = values.includes(String(item.serviceId))
              const canAdd = !disabled && !added
              return (
                <Box
                  key={item.serviceId}
                  role="listitem"
                  tabIndex={disabled ? -1 : 0}
                  aria-disabled={added || disabled ? true : undefined}
                  onClick={() => canAdd && add(item)}
                  onKeyDown={e => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    if (canAdd) add(item)
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '4px',
                    borderBottom: `1px solid ${colors.chipBg}`,
                    padding: '3px 2px',
                    cursor: canAdd ? 'pointer' : 'default',
                    opacity: added ? 0.6 : 1,
                    '&:hover': canAdd ? { backgroundColor: colors.hover } : {},
                    '&:focus-visible': {
                      outline: `2px solid ${colors.text}`,
                      outlineOffset: '-2px',
                    },
                  }}
                >
                  <Typography sx={{ ...textStyle, fontSize: '12px' }}>
                    {consumerLabel(item)}
                  </Typography>
                  {added && (
                    <Box
                      component="span"
                      sx={{
                        ...textStyle,
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px',
                        flexShrink: 0,
                      }}
                    >
                      <CheckIcon sx={{ fontSize: 14 }} />
                      נוסף
                    </Box>
                  )}
                </Box>
              )
            })}
            {(found.hasMore || found.items.length > MAX_RESULTS) && (
              <Typography
                sx={{ ...textStyle, fontSize: '12px', paddingTop: '3px' }}
              >
                יש עוד תוצאות, הקלידו עוד.
              </Typography>
            )}
          </Box>
        )}
      </Box>
      {values.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ ...textStyle, fontSize: '12px' }}>
            {values.length === 1 ? 'צרכן אחד' : `${values.length} צרכנים`}
          </Typography>
          {!disabled && (
            <Button
              className="nodrag"
              size="small"
              onClick={() => onChange([])}
              sx={{
                ...textStyle,
                fontSize: '12px',
                minWidth: 0,
                padding: '0 4px',
              }}
            >
              נקה הכל
            </Button>
          )}
        </Box>
      )}
    </Box>
  )
}

export default NodeConsumerPicker
